// Columns with a line over them, on recharts 3 (ADR 0002): one series as
// columns and one as a line across the same categories, for a figure read
// against its reference (actual as columns, target as a line).
//
// ONE y axis. Both series are drawn on the same scale, so the line crossing
// a column top means the two values are equal. Two series on two different
// scales in one plot is a lie: where the marks meet would depend on how the
// axes were stretched, not on the data. So the contract takes one scale and
// has no second axis to turn on; a pair of series in different units belongs
// in two charts side by side.
//
// The line is told apart by its mark, a line with a ring at every category,
// and by its legend key, the same rule and ring in small beside the square
// of the columns. Colour is never the only difference.
import { Bar, CartesianGrid, ComposedChart, Line, Tooltip, XAxis, YAxis } from "recharts";
import { ChartFigure, ChartTooltip, defaultFormat, type ChartBaseProps, type ChartHeight } from "./frame";
import { CATEGORY_TICK_MARGIN, CategoryFrame, categoryAxisProps, planCategoryAxis, tickAxisWidth, type TrendDatum, type TrendSeries } from "./Cartesian";
import { CHART_FILL, CHART_TICK, LineKey, seriesColor, seriesValue } from "./Grouped";

export interface ComboChartProps extends Omit<ChartBaseProps, "valueLabel"> {
  /**
   * Categories in their natural order; `label` is the x value. The axis is
   * the one ColumnChart draws: 8 characters or less on one line, a longer
   * label wrapped onto two (columnLabelsFit says whether a set prints at
   * every width).
   */
  data: TrendDatum[];
  /** The series drawn as columns, e.g. the actual. */
  bars: TrendSeries;
  /**
   * The series drawn as a line over the columns, e.g. the target. It shares
   * the y scale and the unit of the columns: one `format`, one axis.
   */
  line: TrendSeries;
  height?: ChartHeight;
}

const BAR_COLOR = seriesColor(0);
const LINE_COLOR = seriesColor(1);

/**
 * One series as columns, one as a line, same categories, same y scale.
 * The plot stretches to the height its card is given.
 */
export function ComboChart({ label, summary, categoryLabel, format = defaultFormat, data, bars, line, height = "md" }: ComboChartProps) {
  const values = data.flatMap((d) => [seriesValue(d, bars.key), seriesValue(d, line.key)]);
  const labels = data.map((d) => d.label);
  const yWidth = tickAxisWidth(values, format);
  const plan = planCategoryAxis(labels, yWidth);
  const table = {
    caption: label,
    head: [categoryLabel, bars.label, line.label],
    rows: data.map((d) => [d.label, format(seriesValue(d, bars.key)), format(seriesValue(d, line.key))]),
  };
  const legend = (
    <ul className="con-legend" aria-hidden="true">
      <li className="con-legend-item">
        <span className="con-swatch" style={{ background: BAR_COLOR }} />
        <span className="con-legend-label">{bars.label}</span>
      </li>
      <li className="con-legend-item">
        <LineKey color={LINE_COLOR} />
        <span className="con-legend-label">{line.label}</span>
      </li>
    </ul>
  );
  return (
    <ChartFigure label={label} summary={summary} kind="combo" height={height} table={table} legend={legend}>
      <CategoryFrame plan={plan} labels={labels}>
        <ComposedChart responsive style={CHART_FILL} data={data} margin={{ top: 8, right: 4, bottom: 0, left: 0 }} barCategoryGap="20%" accessibilityLayer={false}>
          <CartesianGrid vertical={false} />
          {plan.mode === "plain" ? (
            <XAxis dataKey="label" tickLine={false} tick={CHART_TICK} interval="preserveStartEnd" minTickGap={4} tickMargin={CATEGORY_TICK_MARGIN} height={28} />
          ) : (
            <XAxis {...categoryAxisProps(plan)} />
          )}
          <YAxis tickLine={false} axisLine={false} tick={CHART_TICK} tickCount={4} allowDecimals={false} width={yWidth} tickFormatter={(v: number) => format(v)} />
          <Tooltip cursor={{ fill: "var(--color-layer-1)" }} content={<ChartTooltip format={format} />} isAnimationActive={false} />
          <Bar dataKey={bars.key} name={bars.label} fill={BAR_COLOR} radius={[4, 4, 0, 0]} maxBarSize={48} isAnimationActive={false} />
          <Line
            type="linear"
            dataKey={line.key}
            name={line.label}
            stroke={LINE_COLOR}
            strokeWidth={2}
            dot={{ r: 3, strokeWidth: 2, stroke: LINE_COLOR, fill: "var(--color-surface)" }}
            activeDot={{ r: 4, strokeWidth: 0, fill: LINE_COLOR }}
            isAnimationActive={false}
          />
        </ComposedChart>
      </CategoryFrame>
    </ChartFigure>
  );
}
